import { useState } from 'react';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { Box, Grid, Typography, makeStyles } from '@material-ui/core';
import type { RepoCiHealth } from './ciSnapshot';
import { useCardError, useHomeSources } from './homeSources';
import { formatDuration } from './formatDuration';
import { CustomerProjectChip } from '../CustomerProjectChip';
import { FilterSwitch } from './FilterSwitch';
import { searchesShown } from './searchesShown';

// The snapshot's shape is shared with the parts of the home page that read the
// same snapshot — the attention card and the workspace figure.
const useStyles = makeStyles(theme => ({
  kpiRow: {
    marginBottom: theme.spacing(2),
  },
  kpiValue: {
    fontSize: '1.9rem',
    fontWeight: 600,
    lineHeight: 1.1,
  },
  kpiLabel: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  badge: {
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: '0.72rem',
    fontWeight: 600,
    lineHeight: 1.6,
  },
  badgeRed: {
    background: 'rgba(248, 81, 73, 0.15)',
    color: '#F85149',
  },
  badgeSlow: {
    background: 'rgba(210, 153, 34, 0.15)',
    color: '#D29922',
  },
  badgeOk: {
    background: 'rgba(46, 160, 67, 0.15)',
    color: '#2EA043',
  },
}));

function formatPct(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

// The snapshot carries 0 for "no run time could be determined", never for a
// build that finished: that is the unknown AC-15 wants shown as unknown, not as
// a fast build.
const runTime = (sec: number) => formatDuration(sec || null);

const statusLabels = (repo: RepoCiHealth) =>
  [
    repo.isRed && 'Red',
    repo.isSlow && 'Slow',
    !repo.isRed && !repo.isSlow && 'OK',
  ]
    .filter(Boolean)
    .join(' ');

// A repo can be both red and slow, so show every applicable badge rather than
// only the first — otherwise a red-and-slow repo never surfaces that it is slow.
const StatusBadge = ({ repo }: { repo: RepoCiHealth }) => {
  const classes = useStyles();
  const badges: Array<{ key: string; label: string; cls: string }> = [];
  if (repo.isRed) {
    badges.push({ key: 'red', label: 'Red', cls: classes.badgeRed });
  }
  if (repo.isSlow) {
    badges.push({ key: 'slow', label: 'Slow', cls: classes.badgeSlow });
  }
  if (badges.length === 0) {
    badges.push({ key: 'ok', label: 'OK', cls: classes.badgeOk });
  }
  return (
    <Box display="flex" style={{ gap: 4 }} flexWrap="wrap">
      {badges.map(b => (
        <span key={b.key} className={`${classes.badge} ${b.cls}`}>
          {b.label}
        </span>
      ))}
    </Box>
  );
};

/**
 * Org-wide CI health Overview (DX-90). Reads the pre-aggregated snapshot from the
 * github-insights backend (`/ci-health`) — the backend polls GitHub Actions on a
 * schedule, so this widget just renders the cached numbers and never calls GitHub
 * itself. Not filtered to the user's services (that is DX-72 territory); this is a
 * whole-org health view across the discovery orgs.
 */
export const CiHealthOverview = () => {
  const classes = useStyles();
  // The page's one answer for the snapshot, which the attention card and the
  // workspace read too: their build rows and the CI figure lead here, and the
  // repositories they count as failing have to be the ones this card marks
  // red (DX-307).
  const { ci } = useHomeSources();
  const { loading, data } = ci;
  const error = useCardError(ci.error, 'CI health');
  const [hideCustomerProjects, setHideCustomerProjects] = useState(false);

  if (loading) {
    return (
      <InfoCard title="CI health">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="CI health">
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  // First poll not finished yet — the snapshot has no data.
  if (!data || data.generatedAt === null) {
    return (
      <InfoCard title="CI health">
        <Typography variant="body2" color="textSecondary">
          Warming up — the first CI health snapshot is still being collected.
          Check back in a moment.
        </Typography>
      </InfoCard>
    );
  }

  const columns: TableColumn<RepoCiHealth>[] = [
    {
      title: 'Status',
      field: 'isRed',
      width: '110px',
      render: row => <StatusBadge repo={row} />,
      customFilterAndSearch: searchesShown<RepoCiHealth>(statusLabels),
    },
    {
      title: 'Repository',
      field: 'slug',
      render: row => (
        <>
          {row.lastRun ? (
            <Link to={row.lastRun.htmlUrl}>{row.slug}</Link>
          ) : (
            row.slug
          )}
          {row.customerProject && <CustomerProjectChip />}
        </>
      ),
    },
    {
      title: 'Branch',
      field: 'lastRun.headBranch',
      width: '120px',
      render: row => row.lastRun?.headBranch ?? '—',
    },
    {
      title: 'Success',
      field: 'successRate',
      width: '100px',
      render: row => formatPct(row.successRate),
      customFilterAndSearch: searchesShown<RepoCiHealth>(row =>
        formatPct(row.successRate),
      ),
    },
    {
      title: 'Avg run',
      field: 'avgDurationSec',
      width: '100px',
      render: row => runTime(row.avgDurationSec),
      customFilterAndSearch: searchesShown<RepoCiHealth>(row =>
        runTime(row.avgDurationSec),
      ),
    },
    {
      title: 'Runs',
      field: 'runs',
      width: '72px',
    },
  ];

  const generatedAt = new Date(data.generatedAt).toLocaleTimeString();
  // The list narrows; the figures above it keep counting every repository,
  // as the Deployments page's do (specs/customer-projects.md AC-4).
  const rows = hideCustomerProjects
    ? data.repos.filter(repo => !repo.customerProject)
    : data.repos;
  const hidden = data.repos.length - rows.length;
  // A switch only where there is one to hide: a switch that never changes the
  // list reads as a filter that is broken.
  const hasCustomerProjects = data.repos.some(repo => repo.customerProject);
  const filterNote = hideCustomerProjects ? ' · no customer projects' : '';

  return (
    <InfoCard
      title="CI health"
      subheader={`${data.orgs.join(', ')} · updated ${generatedAt}${filterNote}`}
      action={
        hasCustomerProjects ? (
          <Box pr={2}>
            <FilterSwitch
              label="Hide customer projects"
              ariaLabel="Hide customer projects"
              checked={hideCustomerProjects}
              onChange={setHideCustomerProjects}
            />
          </Box>
        ) : undefined
      }
    >
      <Grid container spacing={2} className={classes.kpiRow}>
        <Grid item xs={4}>
          <Typography className={classes.kpiValue}>
            {formatPct(data.totals.successRate)}
          </Typography>
          <Typography className={classes.kpiLabel}>Success rate</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography className={classes.kpiValue}>
            {runTime(data.totals.avgDurationSec)}
          </Typography>
          <Typography className={classes.kpiLabel}>Avg run time</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography className={classes.kpiValue}>
            {data.totals.repos}
          </Typography>
          <Typography className={classes.kpiLabel}>Repos with CI</Typography>
        </Grid>
      </Grid>

      <Table<RepoCiHealth>
        columns={columns}
        data={rows}
        options={{
          // Counted over the snapshot rather than over the rows on screen, so
          // the field does not vanish, its text still applied, when the switch
          // leaves five rows or fewer.
          search: data.repos.length > 5,
          toolbar: data.repos.length > 5,
          showTitle: false,
          searchFieldAlignment: 'left',
          paging: rows.length > 10,
          pageSize: 10,
          // A search leaving one row leaves one row, not nine blank ones.
          emptyRowsWhenPaging: false,
          padding: 'dense',
        }}
        emptyContent={
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">
              {hidden > 0
                ? `Each of the ${hidden} repositories with CI is a customer project, and those are hidden.`
                : 'No workflow runs found across the tracked orgs.'}
            </Typography>
          </Box>
        }
      />
      {/* Said under the table, as the deployments card does: a filter whose
          effect cannot be seen is one a reader stops trusting. */}
      {hidden > 0 && rows.length > 0 && (
        <Box px={2} pb={1}>
          <Typography variant="caption" color="textSecondary">
            {hidden}{' '}
            {hidden === 1 ? 'customer project is' : 'customer projects are'}{' '}
            hidden.
          </Typography>
        </Box>
      )}
    </InfoCard>
  );
};
